import { db } from "db/db";
import { Revision } from "db/definitions";
import batchCreateRevisions from "utils/batchCreateRevisions";
import batchRetrieveRevisions from "utils/batchRetrieveRevisions";
import batchRetrieveRevisionsByFormGroup from "utils/batchRetrieveRevisionsByFormGroup";
import { handleBatchCrud } from "utils/handleBatchCrud";
import { recordSentry } from "utils/recordSentry";
import { v4 as uuid } from "uuid";

/**
 * Put array of revisions.
 * @param revisions that need to be created.
 * @param online status
 */
export async function putRevisions(revisions: Revision[], online: boolean) {
	try {
		if (online) {
			await handleBatchCrud(revisions, batchCreateRevisions, 25);
		} else {
			//offline
			await bulkPutRevisionsOffline(revisions);
		}
	} catch (error) {
		recordSentry({ error });
	}
}

/**
 * Batch creates revisions offline.
 * @param revisions Revisions the be created.
 */
async function bulkPutRevisionsOffline(revisions: Revision[]) {
	try {
		await db.revision.bulkPut(revisions);
	} catch (error) {
		recordSentry({ error });
	}
}

/**
 * removes the revisions offline by id.
 * @param revisionIds ids of the revisions to remove offline
 */
export async function bulkDeleteRevisionsOffline(revisionIds: string[]) {
	try {
		await db.revision.bulkDelete(revisionIds);
	} catch (error) {
		recordSentry({ error });
	}
}

/**
 * Retrieves revisions by field instance id.
 * @param fieldInstanceId id of the field instance that the revisions are needed for.
 * @param online status
 * @returns the revisions
 */
export async function getRevisionsByFieldInstance(fieldInstanceId: string, online: boolean): Promise<Revision[]> {
	try {
		if (online) {
			// Need to make sure to paginate this:
			const revisions = await batchRetrieveRevisions(fieldInstanceId);
			return revisions;
		} else {
			//offline
			const revisions = await getRevisionsOfflineByFieldInstanceId(fieldInstanceId);
			return revisions;
		}
	} catch (error) {
		recordSentry({ error });
	}
	return [];
}

export async function getRevisionsByFormGroup(formGroupId: string, online: boolean): Promise<Revision[]> {
	try {
		if (online) {
			// Need to make sure to paginate this:
			const revisions = await batchRetrieveRevisionsByFormGroup(formGroupId);
			return revisions;
		} else {
			//offline
			const revisions = await getRevisionsOfflineByFormGroup(formGroupId);
			return revisions;
		}
	} catch (error) {
		recordSentry({ error });
	}
	return [];
}

/**
 * gets all the revisions for a corresponding field instance by id.
 * @param fieldInstanceId id of the field instance
 * @returns reviisions
 */
async function getRevisionsOfflineByFieldInstanceId(fieldInstanceId: string) {
	try {
		const offlineRevisions = await db.revision.where("fieldInstance").equals(fieldInstanceId).toArray();
		return offlineRevisions;
	} catch (error) {
		recordSentry({ error });
	}
	return [];
}

/**
 * gets all the revisions for a corresponding formGroup by id.
 * @param formGroupId id of the form group
 * @returns revisions
 */
async function getRevisionsOfflineByFormGroup(formGroupId: string) {
	try {
		const offlineRevisions = await db.revision.where("formGroup").equals(formGroupId).toArray();
		return offlineRevisions;
	} catch (error) {
		recordSentry({ error });
	}
	return [];
}

/**
 * creates a revision document ready for updating in the cloud.
 * @param RevisionDoc basically pass in the revision object and this will be set up.
 */
export function createRevisionForBatchResolver(revision: Pick<Revision, "author" | "fieldInstance" | "formGroup" | "group" | "value"> & Partial<Revision>) {
	const timestamp = new Date().toISOString();
	const newRevision = {
		id: revision.id || uuid(),
		fieldInstance: revision.fieldInstance,
		formGroup: revision.formGroup,
		value: revision.value,
		createdAt: revision.createdAt || timestamp,
		updatedAt: revision.updatedAt || timestamp,
		author: revision.author,
		group: revision.group,
	};
	return newRevision;
}
